import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import PreviewMenuView from "@/components/menu/PreviewMenuView";
import { loadCustomerExperience } from "@/lib/customerExperience";

const normalizeIds = (ids = []) =>
  Array.isArray(ids)
    ? ids.filter((id) => id !== null && id !== undefined).map(String)
    : [];

const getBusinessSubDomain = (business = {}, fallback = "") =>
  business.subDomain ??
  business.SubDomain ??
  business.businessSubDomain ??
  business.BusinessSubDomain ??
  business.business_sub_domain ??
  fallback;

const getBusinessName = (business = {}) =>
  business.name ??
  business.Name ??
  business.businessName ??
  business.BusinessName ??
  "";

const getBusinessLogo = (business = {}) =>
  business.logo_url ??
  business.logoUrl ??
  business.LogoUrl ??
  business.businessLogo ??
  business.BusinessLogo ??
  business.logo ??
  business.Logo ??
  "";

const normalizePromotions = (promotions = []) =>
  promotions.map((promotion) => {
    const rawProducts = promotion.products ?? promotion.Products ?? [];

    const selectedProductIds =
      promotion.productIds ??
      promotion.ProductIds ??
      promotion.selectedProductIds ??
      promotion.SelectedProductIds ??
      rawProducts.map((product) => product.id ?? product.Id) ??
      [];

    return {
      id: promotion.id ?? promotion.Id,
      title: promotion.name ?? promotion.Name ?? "",
      description: promotion.description ?? promotion.Description ?? "",
      image_url:
        promotion.image_url ??
        promotion.imageUrl ??
        promotion.ImageUrl ??
        promotion.imageFile?.fileUrl ??
        promotion.ImageFile?.fileUrl ??
        "",
      imageId: promotion.imageId ?? promotion.ImageId ?? null,
      is_active: promotion.visible ?? promotion.Visible ?? true,
      selectedProductIds: normalizeIds(selectedProductIds),
      startDate: promotion.beginDate ?? promotion.BeginDate ?? "",
      endDate: promotion.endDate ?? promotion.EndDate ?? "",
      order:
        promotion.sortOrder ??
        promotion.SortOrder ??
        promotion.index ??
        promotion.Index ??
        0,
      raw: promotion,
    };
  });

const removeDuplicateProducts = (products = []) => {
  const map = new Map();

  products.forEach((product) => {
    const productId = product?.id ?? product?.Id;

    if (productId == null) return;

    map.set(String(productId), product);
  });

  return Array.from(map.values());
};

export default function PublicMenuPage() {
  const params = useParams();

  const slug = useMemo(
    () => params.slug || window.location.pathname.split("/").filter(Boolean)[0] || "",
    [params.slug]
  );

  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [banners, setBanners] = useState([]);
  const [businessInfo, setBusinessInfo] = useState({
    name: "",
    subDomain: "",
    logoUrl: "",
  });
  const [customerPopupData, setCustomerPopupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setError("Restoran bulunamadı");
      setLoading(false);
      return;
    }

    let isMounted = true;

    const loadPublicMenu = async () => {
      try {
        setLoading(true);
        setError(null);

        const businessRes = await api.getBusinessBySubDomain(slug);
        const business = businessRes?.data || {};
        const businessId = business.id ?? business.Id;

        if (!businessId) {
          throw new Error("Restoran bulunamadı");
        }

        const businessSubDomain = getBusinessSubDomain(business, slug);

        if (!isMounted) return;

        setBusinessInfo({
          name: getBusinessName(business),
          subDomain: businessSubDomain,
          logoUrl: getBusinessLogo(business),
          address:
            business?.address ||
            business?.Address ||
            business?.businessAddress ||
            business?.BusinessAddress ||
            business?.location ||
            business?.Location ||
            business?.business?.address ||
            business?.business?.Address ||
            business?.Business?.Address ||
            "-",
        });

        setCustomerPopupData(loadCustomerExperience(businessSubDomain));

        const [categoriesRes, promotionsRes] = await Promise.all([
          api.getPublicCategories({
            businessSubDomain,
            page: 0,
            pageSize: 1000,
          }),

          api.getPublicPromotions({
            businessSubDomain,
            page: 0,
            pageSize: 1000,
          }),
        ]);

        const categoriesData = categoriesRes?.data || [];
        const promotionsData = normalizePromotions(promotionsRes?.data || []);

        const productResponses = await Promise.all(
          categoriesData.map((category) =>
            api
              .getPublicMenuItems({
                businessSubDomain,
                categorySlug: category.slug,
                page: 0,
                pageSize: 1000,
              })
              .then((res) => res?.data || [])
              .catch((err) => {
                console.warn(
                  "Kategori ürünleri alınamadı:",
                  category?.id,
                  err
                );
                return [];
              })
          )
        );

        const productsData = removeDuplicateProducts(productResponses.flat());

        if (!isMounted) return;

        setCategories(categoriesData);
        setMenuItems(productsData);
        setBanners(promotionsData);
      } catch (err) {
        console.error("Public menu hata:", err);

        if (isMounted) {
          setError("Restoran bulunamadı");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPublicMenu();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f7f7] px-4">
        <div className="space-y-3 text-center">
          <h1 className="text-2xl font-bold text-zinc-900">
            Restoran bulunamadı
          </h1>

          <p className="text-zinc-600">
            Görüntülemeye çalıştığınız menü mevcut değil ya da kaldırılmış.
          </p>
        </div>
      </div>
    );
  }

  return (
    <PreviewMenuView
      categories={categories}
      menuItems={menuItems}
      banners={banners}
      businessInfo={businessInfo}
      customerPopupData={customerPopupData}
      loading={loading}
      loadingText="Menü yükleniyor..."
      emptyText="Menü yakında yayında olacak."
      audience="public"
    />
  );
}